import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clientsApi } from '../lib/api';

interface Client {
  id: string;
  slug: string;
  companyName: string;
  websiteUrl: string;
  city: string;
  niche: string;
  logoUrl: string | null;
  isActive: boolean;
  integrations: {
    wordpress: boolean;
    youtube: boolean;
    podbean: boolean;
    facebook: boolean;
    gbp: boolean;
  };
}

export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  async function loadClients() {
    try {
      const res = await clientsApi.list();
      setClients(res.clients);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-600">Manage your client profiles and credentials</p>
        </div>
        <Link
          to="/clients/new"
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Add Client
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Client Grid */}
      {clients.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No clients yet</h3>
          <p className="text-gray-500 mb-4">
            Get started by adding your first client
          </p>
          <Link
            to="/clients/new"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Add Client
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client) => (
            <ClientCard key={client.id} client={client} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClientCard({ client }: { client: Client }) {
  const integrationCount = Object.values(client.integrations).filter(Boolean).length;

  return (
    <Link
      to={`/clients/${client.slug}`}
      className="block bg-white rounded-lg shadow hover:shadow-md transition-shadow"
    >
      <div className="p-6">
        {/* Logo / Initial */}
        <div className="flex items-start justify-between mb-4">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
            {client.logoUrl ? (
              <img
                src={client.logoUrl}
                alt={client.companyName}
                className="w-10 h-10 object-contain rounded"
              />
            ) : (
              <span className="text-primary-600 font-bold text-lg">
                {client.companyName.charAt(0)}
              </span>
            )}
          </div>
          <span
            className={`text-xs px-2 py-1 rounded-full ${
              client.isActive
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {client.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Info */}
        <h3 className="font-semibold text-gray-900 mb-1 truncate">
          {client.companyName}
        </h3>
        <p className="text-sm text-gray-500 mb-3">
          {client.city} • {client.niche}
        </p>

        {/* Integrations */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            <IntegrationIcon
              active={client.integrations.wordpress}
              label="WP"
              title="WordPress"
            />
            <IntegrationIcon
              active={client.integrations.youtube}
              label="YT"
              title="YouTube"
            />
            <IntegrationIcon
              active={client.integrations.podbean}
              label="PB"
              title="Podbean"
            />
            <IntegrationIcon
              active={client.integrations.facebook}
              label="FB"
              title="Facebook"
            />
            <IntegrationIcon
              active={client.integrations.gbp}
              label="GB"
              title="Google Business"
            />
          </div>
          <span className="text-xs text-gray-400">
            {integrationCount}/5 configured
          </span>
        </div>
      </div>
    </Link>
  );
}

function IntegrationIcon({
  active,
  label,
  title,
}: {
  active: boolean;
  label: string;
  title: string;
}) {
  return (
    <div
      title={`${title}: ${active ? 'Configured' : 'Not configured'}`}
      className={`w-6 h-6 rounded text-xs font-medium flex items-center justify-center ${
        active
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-400'
      }`}
    >
      {label}
    </div>
  );
}
